<template>
    <hts-drawer
        v-model="visible"
        color="main"
        size="large"
        :title="$t('labels.edit')"
        :submit="handleEditDebit"
        @close="close">
        <hts-select-option
            v-model="formData.type"
            :items="types"
            :label="$t('labels.type')"
            readonly />
        <div
            v-if="!loading"
            class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <template v-if="['pobo'].includes(formData.type)">
                <hts-select-cooperator
                    v-model="formData.recipientId"
                    v-model:name="formData.recipientName"
                    :label="$t('labels.payTo')"
                    required
                    @change="formData.payToInvoicingInformationId = formData.payToInvoicingInformationName = ''" />
                <hts-select-cooperator-invoicing-information
                    v-model="formData.payToInvoicingInformationId"
                    v-model:name="formData.payToInvoicingInformationName"
                    :disabled="!formData.recipientId"
                    :cooperator-id="formData.recipientId"
                    :label="$t('labels.selectInvoicingInformation')"
                    :creatable="true"
                    required
                    prop="payTo"
                    name-select="payTo" />
                <hts-select-cooperator
                    v-model="formData.recipient2Id"
                    v-model:name="formData.recipient2Name"
                    :label="$t('labels.collectFrom')"
                    required
                    @change="formData.collectFromInvoicingInformationId = formData.collectFromInvoicingInformationName = ''" />
                <hts-select-cooperator-invoicing-information
                    v-model="formData.collectFromInvoicingInformationId"
                    v-model:name="formData.collectFromInvoicingInformationName"
                    :disabled="!formData.recipient2Id"
                    :cooperator-id="formData.recipient2Id"
                    :label="$t('labels.selectInvoicingInformation')"
                    :creatable="true"
                    required
                    prop="collectFrom"
                    name-select="collectFrom" />
                <hts-form-input
                    v-model="formData.invoiceNo"
                    :label="$t('labels.invoiceNo')"
                    required />
            </template>
            <template v-else-if="['cobo'].includes(formData.type)">
                <hts-select-cooperator
                    v-model="formData.recipient2Id"
                    v-model:name="formData.recipient2Name"
                    :label="$t('labels.payTo')"
                    required
                    @change="formData.payToInvoicingInformationId = formData.payToInvoicingInformationName = ''" />
                <hts-select-cooperator-invoicing-information
                    v-model="formData.payToInvoicingInformationId"
                    v-model:name="formData.payToInvoicingInformationName"
                    :disabled="!formData.recipient2Id"
                    :cooperator-id="formData.recipient2Id"
                    :label="$t('labels.selectInvoicingInformation')"
                    :creatable="true"
                    required
                    prop="payTo"
                    name-select="payTo" />
                <hts-select-cooperator
                    v-model="formData.recipientId"
                    v-model:name="formData.recipientName"
                    :label="$t('labels.collectFrom')"
                    required
                    @change="formData.collectFromInvoicingInformationId = formData.collectFromInvoicingInformationName = ''" />
                <hts-select-cooperator-invoicing-information
                    v-model="formData.collectFromInvoicingInformationId"
                    v-model:name="formData.collectFromInvoicingInformationName"
                    :disabled="!formData.recipientId"
                    :cooperator-id="formData.recipientId"
                    :label="$t('labels.selectInvoicingInformation')"
                    :creatable="true"
                    required
                    prop="collectFrom"
                    name-select="collectFrom" />
                <hts-form-input
                    v-model="formData.invoiceNo"
                    :label="$t('labels.invoiceNo')"
                    required />
            </template>
            <template v-else>
                <template v-if="['revenue'].includes(formData.type)">
                    <hts-form-input
                        v-model="formData.customerName"
                        :label="$t('labels.customer')"
                        readonly />
                </template>
                <template v-if="['cost'].includes(formData.type)">
                    <hts-select-cooperator
                        v-model="formData.providerId"
                        v-model:name="formData.providerName"
                        :label="$t('labels.provider')"
                        required
                        @change="formData.selectInvoicingInformation = formData.selectInvoicingInformationName = null" />
                </template>
                <hts-select-cooperator-invoicing-information
                    v-model="formData.selectInvoicingInformation"
                    v-model:name="formData.selectInvoicingInformationName"
                    :disabled="!formData.customerId"
                    :cooperator-id="formData.customerId"
                    :label="$t('labels.selectInvoicingInformation')"
                    :creatable="true"
                    required
                    prop="contact"
                    name-select="contact" />
            </template>

            <hts-form-date
                v-model="formData.invoicedDate"
                :label="$t('labels.invoicedDate')" />

            <hts-form-date
                v-model="formData.dueDate"
                :label="$t('labels.dueDate')" />

            <hts-form-select
                v-model="formData.currency"
                :items="currencyOptions"
                :label="$t('labels.currency')" />

            <hts-form-select
                v-model="formData.paymentMethod"
                :items="paymentMethodOptions"
                :label="$t('labels.paymentMethod')" />

            <hts-select-bank-account
                v-if="formData.paymentMethod === 'bank'"
                v-model="formData.bankAccountId"
                v-model:name="formData.bankAccountInfo"
                creatable />
        </div>

        <charge-table
            v-model="formData.prices"
            v-model:total-amount="totalAmount"
            v-model:total-tax="totalTax"
            v-model:grand-total="grandTotal" />
    </hts-drawer>
</template>
<script setup lang="ts">
import ChargeTable from './-components/change-table.vue'
const visible = ref<boolean>(false)
const emits = defineEmits(['change'])
const types = ref([
  { label: $t('labels.revenue'), value: 'revenue' },
  { label: $t('labels.cost'), value: 'cost' },
  { label: $t('labels.pobo'), value: 'pobo' },
  { label: $t('labels.cobo'), value: 'cobo' }
  // { label: 'Tạm ứng', value: 'advance' },
  // { label: 'Hoàn ứng', value: 'repayment' }
])
const formData = ref<any>({
  type: 'cost',
  recipientFrom: '',
  providerId: '',
  providerName: '',
  selectInvoicingInformation: '',
  selectInvoicingInformationName: '',
  recipientId: null,
  recipientName: null,
  payToInvoicingInformationId: '',
  payToInvoicingInformationName: '',
  recipient2Id: null,
  recipient2Name: null,
  collectFromInvoicingInformationId: '',
  collectFromInvoicingInformationName: '',
  invoiceNo: '',
  invoicedDate: '',
  dueDate: '',
  currency: 'VND',
  paymentMethod: '',
  prices: [] as { [key: string]: any }[],
  bankAccountId: '',
  bankAccountInfo: '',
  totalAmount: 0,
  totalTax: 0,
  grandTotal: 0
})

const resetForm = () => {
  formData.value = {
    type: 'cost',
    recipientFrom: 'CUSTOMER',
    providerId: '',
    providerName: '',
    selectInvoicingInformation: '',
    selectInvoicingInformationName: '',
    recipientId: null,
    recipientName: null,
    payToInvoicingInformationId: '',
    payToInvoicingInformationName: '',
    recipient2Id: null,
    recipient2Name: null,
    collectFromInvoicingInformationId: '',
    collectFromInvoicingInformationName: '',
    invoiceNo: '',
    invoicedDate: '',
    dueDate: '',
    currency: 'VND',
    paymentMethod: '',
    prices: [] as { [key: string]: any }[],
    bankAccountId: '',
    bankAccountInfo: '',
    totalAmount: 0,
    totalTax: 0,
    grandTotal: 0
  }
}

const tripId = ref('')
const debitId = ref('')

const loading = ref(false)
const open = (tId: string, dId: string) => {
  visible.value = true
  tripId.value = tId
  debitId.value = dId
  loading.value = true
  getData(tId, dId)
}
const getData = async (tId: string, dId: string) => {
  const api = {
    url: `/trip/${tId}/billing-and-costing/payment-note/${dId}`,
    method: 'get'
  }
  const data: any = await $api(api)
  const resData: any = data.data.value

  if (resData?.success) {
    formData.value = resData.data
    nextTick(() => {
      loading.value = false
    })
  }
}

const currencyOptions = [{ label: 'VND', value: 'VND' }]

const paymentMethodOptions = [
  { label: 'Tiền mặt', value: 'cash' },
  { label: 'Chuyển khoản', value: 'bank' }
]

const totalAmount = ref(0)
const totalTax = ref(0)
const grandTotal = ref(0)

const close = () => {
  resetForm()
  visible.value = false
}

const handleEditDebit = async () => {
  const api = {
    url: `/trip/tripId/billing-and-costing/payment-note/debitId`,
    method: 'put'
  }

  formData.value.totalAmount = totalAmount.value
  formData.value.totalTax = totalTax.value
  formData.value.grandTotal = grandTotal.value

  const data: any = await $api(api, { uriParams: { tripId: tripId.value, debitId: debitId.value }, body: formData.value })
  const resData = data?.data?.value

  if (resData && resData.success) {
    if (resData.success) {
      process.client && $toast().success($t('labels.editSuccess'))
      close()
      emits('change')
    }
  }
}

defineExpose({ open })
</script>
